import type Receipt                 from '@/domains/Receipts/Receipt';
import Title_ru_RU_Class            from "@/core/misc/decorators/Title_ru_RU_Class";
import Core                         from "@/core/Core";
import AbstractDocumentPosition     from '@/domains/Abstract/AbstractDocumentPosition';
import { ReceiptComplaintPosition } from '@/domains/Receipts/ReceiptComplaintPosition';
import ShipmentPosition             from '@/domains/Shipments/ShipmentPosition';
import ComplaintTypesService        from '@/domains/ComplaintType/ComplaintTypesService';
import Qty                          from "@/core/types/Qty";

/**
 * Позиция приемки
 */
@Title_ru_RU_Class('Позиция приемки')
class ReceiptPosition extends AbstractDocumentPosition {

  public shipmentPosition: ShipmentPosition;
  public complaints: ReceiptComplaintPosition[];

  /** Принятое не совпадает с отгруженным. */
  get hasMismatch(): boolean {
    if (this.shipmentPosition) {
      return this.qty.equals(this.shipmentPosition.qty) === false;
    } else {
      return false;
    }
  }

  get hasComplaint(): boolean {
    return this.complaints.length > 0;
  }

  /** Количество претензии данного типа (0, если претензии нет). */
  getComplaintQty(typeId: string): Qty {
    const complaint = this.complaints.find(c => c.type?.id === typeId);
    return complaint?.qty ?? Qty.zero();
  }

  /**
   * Правка количества претензии данного типа по id.
   * qty пустой/0 — удаляет претензию этого типа;
   * qty есть — создаёт новую (тип берём из словаря) или обновляет существующую.
   */
  setComplaintQty(typeId: string, qty: Qty): void {
    const index = this.complaints.findIndex(c => c.type?.id === typeId);

    if (qty === null || qty.isEmpty() || qty.isZero()) {
      if (index !== -1) this.complaints.splice(index, 1);
      return;
    }

    if (index !== -1) {
      this.complaints[index].qty = qty;
      return;
    }

    const type = ComplaintTypesService.getById(typeId);
    if (!type) return;

    this.complaints.push(ReceiptComplaintPosition.fromComplaintType(this.orderPosition, type, qty));
  }

  /** Правка принятого количества с пересчётом недостачи. */
  setReceivedQty(qty: Qty | null): void {
    this.qty = qty;
    this.recalculateShortage();
  }

  /** Правка претензии другого типа (не недостачи) с пересчётом недостачи. */
  setOtherComplaintQty(typeId: string, qty: Qty): void {
    this.setComplaintQty(typeId, qty);
    this.recalculateShortage();
  }

  /**
   * Недостача = отгружено − принято − прочие претензии (кроме излишка);
   * если результат не положительный — недостачу не выводим.
   */
  recalculateShortage(): void {
    if (this.qty === null) {
      this.setComplaintQty('shortage', Qty.zero());
    }
    else {
      let shortage = this.shipmentPosition.qty.minus(this.qty);

      for (const complaint of this.complaints) {
        if (complaint.type?.id === 'shortage') {
          // сама недостача
        }
        else if (complaint.type?.id === 'excess') {
          // излишек — принято сверх отгруженного, в расхождение не входит
        }
        else {
          shortage = shortage.minus(complaint.qty);
        }
      }

      if (shortage.isPositive() === true) {
        this.setComplaintQty('shortage', shortage);
      }
      else {
        this.setComplaintQty('shortage', Qty.zero());
      }
    }
  }

  /* ────────────────────────────────────────
     Фабричные методы
     ──────────────────────────────────────── */

  /**
   * Создаёт позицию приёмки из позиции отгрузки.
   * Принятое количество по умолчанию равно отгруженному — оператор скорректирует
   * в гриде/диалоге, если приняли не всё. receipt бэк проставит при сохранении.
   */
  static fromShipmentPosition(shipmentPosition: ShipmentPosition): ReceiptPosition {
    const position = new ReceiptPosition();

    position.orderPosition    = shipmentPosition.orderPosition;
    position.shipmentPosition = shipmentPosition;
    position.qty = shipmentPosition.qty;
    position.complaints = [];

    return position;
  }

  static fromServer(data: any, result = new ReceiptPosition()): ReceiptPosition {
    super.fromServer(data, result);

    result.shipmentPosition = ShipmentPosition.fromServer(data.shipmentPosition);

    result.complaints = Core.utils.hydrateArray(data.complaints, ReceiptComplaintPosition.fromServer);

    return result;
  }

  toString(): string {
    const number = this.orderPosition.formattedNumber;
    return `Позиция ${number}`;
  }
}

export default ReceiptPosition;
